import { Injectable } from '@angular/core';
import { ActivatedRoute, Data } from '@angular/router';
import { ComponentStore, tapResponse } from '@ngrx/component-store';
import { Observable } from 'rxjs';
import { distinctUntilChanged, filter, map, switchMap, withLatestFrom } from 'rxjs/operators';
import { getHouseholdPreference, mapHouseholdToServings, mapToDietaryRequirementOptions } from '../../helpers';
import { MealPlanningService } from '../../services/meal-planning.service';
import { Stamp, MealPlanPreference } from '../../models';
import {
	ActiveMealPlanStep,
	ChooseMealPlanState,
	MealPlanNonPreferenceStepName,
	MealPlanPreferenceName,
	MealPlanStep,
	PreferenceValue,
} from './choose-meal-plan.model';

export const defaultState: ChooseMealPlanState = {
	completed: false,
	steps: [
		{
			name: MealPlanNonPreferenceStepName.splash,
			active: true,
		},
		{
			name: MealPlanPreferenceName.people,
			default: { adult: 2, child: 0 },
			active: false,
		},
		{
			name: MealPlanPreferenceName.dietaryNeeds,
			active: false,
		},
		{
			name: MealPlanNonPreferenceStepName.finalising,
			active: false,
		},
	],
};

@Injectable()
export class ChooseMealPlanStore extends ComponentStore<ChooseMealPlanState> {
	/**
	 * Selectors
	 */
	public readonly activeStep$: Observable<ActiveMealPlanStep> = this.select(({ steps }) => ({
		...this.getActiveStep(steps),
		currentStep: steps.findIndex((step) => step.active),
		totalSteps: steps.length - 2,
	}));

	public readonly mealPlanPreferences$: Observable<MealPlanPreference[]> = this.select(({ steps }) =>
		this.getMealPlanPreferences(steps)
	);

	readonly isFinalising$: Observable<boolean> = this.select(
		({ steps }) => steps.find((step) => step.name === MealPlanNonPreferenceStepName.finalising)?.active || false
	);

	/**
	 * Updaters
	 */
	public readonly applySavedPreferences = this.updater((state, preferences: MealPlanPreference[]) => ({
		...state,
		steps: this.activateLastCompleteStep(this.applyPreferences(state.steps, preferences)),
	}));

	public readonly activateStep = this.updater(
		(state, { stepName, stepNameToSave }: { stepName: string; stepNameToSave?: string }) => ({
			...state,
			steps: state.steps.map((step) => {
				const value = step.name === stepNameToSave ? step.value || step.default : step.value;
				return { ...step, active: step.name === stepName, value };
			}),
		})
	);

	public readonly savePreference = this.updater(
		(state, { stepName, preference }: { stepName: string; preference: PreferenceValue }) => ({
			...state,
			steps: state.steps.map((step) => ({ ...step, value: stepName === step.name ? preference : step.value })),
		})
	);

	public readonly updateDietaryRequirementOptions = this.updater((state, options: { key: number; text: string }[]) => ({
		...state,
		steps: state.steps.map((step) =>
			step.name === MealPlanPreferenceName.dietaryNeeds ? { ...step, options, default: [String(options[0].key)] } : step
		),
	}));

	/**
	 * Effects
	 */
	public readonly restorePreferences = this.effect(() =>
		this.activatedRoute.data.pipe(
			tapResponse(
				(data: Data) =>
					data.mealPlanning?.preferences ? this.applySavedPreferences(data.mealPlanning.preferences) : undefined,
				() => {
					/* fail silently */
				}
			)
		)
	);

	public readonly persistPreferences = this.effect((trigger$: Observable<Stamp | undefined>) =>
		trigger$.pipe(
			withLatestFrom(this.mealPlanPreferences$),
			distinctUntilChanged(),
			filter(([_, preferences]) => !!preferences.length),
			switchMap(([mealToSave, preferences]) => {
				const addedServings = mapHouseholdToServings(getHouseholdPreference(preferences));
				return this.mealPlanningService.persistMealPlanningData({
					preferences,
					selectedMeals: [...(mealToSave ? [{ ...mealToSave, mealPlan: { addedServings } }] : [])],
				});
			})
		)
	);

	public readonly loadDietaryRequirentOptions = this.effect(() =>
		this.mealPlanningService.fetchMealPlanTypes().pipe(
			map(mapToDietaryRequirementOptions),
			tapResponse(
				(dietaryRequirementOptions) => {
					this.updateDietaryRequirementOptions(dietaryRequirementOptions);
				},
				() => {
					/* fail silently */
				}
			)
		)
	);

	constructor(
		private readonly activatedRoute: ActivatedRoute,
		private readonly mealPlanningService: MealPlanningService
	) {
		super(defaultState);
	}

	private getActiveStep(steps: MealPlanStep[]): MealPlanStep {
		return steps.find((step) => step.active) || steps[0];
	}

	private getMealPlanPreferences(steps: MealPlanStep[]): MealPlanPreference[] {
		return steps.reduce(
			(acc: MealPlanPreference[], step: MealPlanStep): MealPlanPreference[] =>
				!!step.value ? [...acc, { name: step.name, value: step.value }] : acc,
			[]
		);
	}

	private applyPreferences(steps: MealPlanStep[], preferences: MealPlanPreference[]): MealPlanStep[] {
		return steps.map((step) => ({
			...step,
			...preferences.filter((preference) => !!preference.value).find((preference) => preference.name === step.name),
		}));
	}

	private activateLastCompleteStep(steps: MealPlanStep[]): MealPlanStep[] {
		const stepToActivate = steps
			.slice()
			.reverse()
			.find((step) => !!step.value);
		const indexToActivate = stepToActivate ? steps.indexOf(stepToActivate) : 0;

		return steps.map((step, index) => ({ ...step, active: index === indexToActivate }));
	}
}
